<template>
  <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
    <label class="flex flex-col gap-1 text-sm text-[#2e234b]">
      {{ $t("ticketPurchase.guest.firstName") }}
      <input
        :value="guest?.firstName"
        type="text"
        autocomplete="given-name"
        class="min-h-11 rounded-lg border px-3 py-2 focus:outline-none focus:ring focus:ring-pink-200"
        @input="
          setField('firstName', ($event.target as HTMLInputElement).value)
        "
      />
    </label>
    <label class="flex flex-col gap-1 text-sm text-[#2e234b]">
      {{ $t("ticketPurchase.guest.lastName") }}
      <input
        :value="guest?.lastName"
        type="text"
        autocomplete="family-name"
        class="min-h-11 rounded-lg border px-3 py-2 focus:outline-none focus:ring focus:ring-pink-200"
        @input="setField('lastName', ($event.target as HTMLInputElement).value)"
      />
    </label>
    <label class="flex flex-col gap-1 text-sm text-[#2e234b]">
      {{ $t("ticketPurchase.guest.email") }}
      <input
        :value="guest?.email"
        type="email"
        inputmode="email"
        autocomplete="email"
        class="min-h-11 rounded-lg border px-3 py-2 focus:outline-none focus:ring focus:ring-pink-200"
        @input="setField('email', ($event.target as HTMLInputElement).value)"
      />
    </label>
    <label class="flex flex-col gap-1 text-sm text-[#2e234b]">
      {{ $t("ticketPurchase.guest.ageGroup") }}
      <div class="relative">
        <select
          :value="guest?.ageGroup"
          class="min-h-11 w-full appearance-none rounded-lg border px-3 py-2 pr-10 focus:outline-none focus:ring"
          :class="
            ageError
              ? 'border-red-400 focus:ring-red-200'
              : 'focus:ring-pink-200'
          "
          @change="
            setField('ageGroup', ($event.target as HTMLSelectElement).value)
          "
        >
          <option value="" disabled>
            {{ $t("ticketPurchase.guest.selectAgeGroup") }}
          </option>
          <option v-for="age in ageGroupOptions" :key="age" :value="age">
            {{ age }}
          </option>
        </select>
        <span
          class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500"
        >
          <svg
            class="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.51a.75.75 0 01-1.08 0l-4.25-4.51a.75.75 0 01.02-1.06z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
      </div>
      <span v-if="ageError" class="text-xs text-red-600">{{ ageError }}</span>
    </label>
    <label class="flex flex-col gap-1 text-sm text-[#2e234b]">
      {{ $t("ticketPurchase.guest.sex") }}
      <div class="relative">
        <select
          :value="guest?.sex"
          class="min-h-11 w-full appearance-none rounded-lg border px-3 py-2 pr-10 focus:outline-none focus:ring focus:ring-pink-200"
          @change="setField('sex', ($event.target as HTMLSelectElement).value)"
        >
          <option value="" disabled>
            {{ $t("ticketPurchase.guest.select") }}
          </option>
          <option v-for="opt in sexOptions" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>
        <span
          class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500"
        >
          <svg
            class="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.51a.75.75 0 01-1.08 0l-4.25-4.51a.75.75 0 01.02-1.06z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
      </div>
    </label>
  </div>
</template>

<script setup lang="ts">
import type { GuestInfo } from "./types";
import { sexOptions, defaultGuestInfo } from "./types";

const props = defineProps<{
  guest?: GuestInfo;
  ageGroupOptions: string[];
  ageError?: string;
}>();

const emit = defineEmits<{
  (e: "update:guest", value: GuestInfo): void;
}>();

function setField<K extends keyof GuestInfo>(key: K, value: GuestInfo[K]) {
  emit("update:guest", {
    ...(props.guest ?? defaultGuestInfo()),
    [key]: value,
  });
}
</script>
